import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Twitter, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { BRAND, LEGAL } from "../lib/brand";
import TikTokIcon from "./TikTokIcon";

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-[#050505] border-t border-white/5 pt-24 pb-10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-12 md:gap-8 mb-20">
          <div className="col-span-2 md:col-span-2">
            <img src="/logo-maison-douba.png" alt="MAISON DOUBA — Paris" className="h-24 md:h-28 mb-6 object-contain" />
            <p className="text-white/45 font-light max-w-sm leading-relaxed text-sm">
              Maison de sélection dédiée aux amateurs de parfums d'exception. Nous réunissons pour vous les fragrances les plus abouties, servies avec l'exigence d'une conciergerie sur-mesure.
            </p>
            <div className="flex gap-5 mt-8">
              <a data-testid="social-instagram" href={BRAND.instagram} target="_blank" rel="noreferrer noopener" aria-label="Instagram" className="text-white/50 hover:text-[color:var(--gold)] transition-colors"><Instagram size={18} strokeWidth={1.5} /></a>
              <a data-testid="social-tiktok" href={BRAND.tiktok} target="_blank" rel="noreferrer noopener" aria-label="TikTok" className="text-white/50 hover:text-[color:var(--gold)] transition-colors"><TikTokIcon size={18} /></a>
              <a data-testid="social-facebook" href="#" aria-label="Facebook" className="text-white/50 hover:text-[color:var(--gold)] transition-colors"><Facebook size={18} strokeWidth={1.5} /></a>
              <a data-testid="social-twitter" href="#" aria-label="Twitter" className="text-white/50 hover:text-[color:var(--gold)] transition-colors"><Twitter size={18} strokeWidth={1.5} /></a>
              <a data-testid="social-youtube" href="#" aria-label="Youtube" className="text-white/50 hover:text-[color:var(--gold)] transition-colors"><Youtube size={18} strokeWidth={1.5} /></a>
            </div>
          </div>

          <div className="col-span-2 md:col-span-2">
            <p className="label-caps text-[color:var(--gold)] mb-6 text-[10px]">Nous joindre</p>
            <ul className="space-y-4 text-sm text-white/70 font-light">
              <li>
                <a data-testid="footer-email" href={`mailto:${BRAND.email}`} className="flex items-start gap-3 hover:text-[color:var(--gold)] transition-colors">
                  <Mail size={14} strokeWidth={1.4} className="text-[color:var(--gold)] mt-0.5 shrink-0" />
                  <span>{BRAND.email}</span>
                </a>
              </li>
              <li>
                <a data-testid="footer-phone" href={`tel:${BRAND.phone_link}`} className="flex items-start gap-3 hover:text-[color:var(--gold)] transition-colors">
                  <Phone size={14} strokeWidth={1.4} className="text-[color:var(--gold)] mt-0.5 shrink-0" />
                  <span>{BRAND.phone_display}</span>
                </a>
              </li>
              <li>
                <a data-testid="footer-whatsapp" href={BRAND.whatsapp} target="_blank" rel="noreferrer noopener" className="flex items-start gap-3 hover:text-[color:var(--gold)] transition-colors">
                  <MessageCircle size={14} strokeWidth={1.4} className="text-[color:var(--gold)] mt-0.5 shrink-0" />
                  <span>WhatsApp</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3">
                  <MapPin size={14} strokeWidth={1.4} className="text-[color:var(--gold)] mt-0.5 shrink-0" />
                  <address data-testid="footer-address" className="not-italic text-white/60 leading-relaxed">
                    {BRAND.address_line1}<br />
                    {BRAND.address_line2}<br />
                    {BRAND.address_country}
                  </address>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <p className="label-caps text-[color:var(--gold)] mb-6 text-[10px]">Service Client</p>
            <ul className="space-y-3 text-sm text-white/55 font-light">
              <li><Link data-testid="footer-contact-link" to="/contact" className="hover:text-white transition-colors">Nous contacter</Link></li>
              <li><Link to="/carte-cadeau" data-testid="footer-gift-card" className="hover:text-[color:var(--gold)] transition-colors">Cartes cadeaux</Link></li>
              <li><Link to="/galerie" className="hover:text-white transition-colors">Galerie & Coulisses</Link></li>
              <li><a href="/#faq" className="hover:text-white transition-colors">FAQ</a></li>
              <li><Link to="/legal/politique-retour" className="hover:text-white transition-colors">Livraison & Retours</Link></li>
              <li><a href="/#collection" className="hover:text-white transition-colors">Notre collection</a></li>
            </ul>
          </div>

          <div>
            <p className="label-caps text-[color:var(--gold)] mb-6 text-[10px]">Informations légales</p>
            <ul className="space-y-3 text-sm text-white/55 font-light">
              <li><Link data-testid="footer-mentions" to="/legal/mentions-legales" className="hover:text-white transition-colors">Mentions légales</Link></li>
              <li><Link data-testid="footer-cgv" to="/legal/cgv" className="hover:text-white transition-colors">CGV</Link></li>
              <li><Link data-testid="footer-privacy" to="/legal/politique-confidentialite" className="hover:text-white transition-colors">Confidentialité</Link></li>
              <li><Link data-testid="footer-returns" to="/legal/politique-retour" className="hover:text-white transition-colors">Politique de retour</Link></li>
            </ul>
          </div>
        </div>

        <div className="hairline-gold opacity-30 mb-8"></div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40 font-light mb-6">
          <div className="space-y-1">
            <p>
              © 2026 <span className="text-white/60">{LEGAL.company_name}</span> · Marque commerciale <span className="text-white/60">{LEGAL.trademark}</span> · {LEGAL.legal_form} — {LEGAL.owner}
            </p>
            <p>
              SIREN {LEGAL.siren} · SIRET {LEGAL.siret} · Code APE {LEGAL.ape_code} ({LEGAL.ape_label})
            </p>
          </div>
          <div className="flex items-center gap-4 label-caps text-[9px] shrink-0">
            <span>Visa</span>
            <span>Mastercard</span>
            <span>Amex</span>
            <span>Apple Pay</span>
            <span>PayPal</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
